import { IQueryContextDatasourceColumnState } from 'src/app/services/query-context/models/IQueryContext';
import { ICompiledSQLTokens } from './datasource';
import { MultipleSelectComponent } from 'src/app/components/multiple-select/multiple-select.component';
import { DatePickerComponent } from 'src/app/components/date-picker/date-picker.component';
import { ISegmentDatasourceColumnState } from 'src/app/dashboards/common/items/filter/segment-filter/segments';
import { GcpProjectId } from '../gcp-project-id.model';
import { NotImplementedError } from '../error';
import { SegmentSqlTokensCompiler } from './segment-sql-tokens-compiler';

/**
 * Compiles SQL tokens for a MultipleSelect component.
 * @param columnSQL sql of a column
 * @param state state of a column
 * @returns
 */
export function multipleSelectComponentCompileSQLTokens(
    columnSQL: string,
    state: IQueryContextDatasourceColumnState,
    toSql: (
        columnSQL: string,
        state: IQueryContextDatasourceColumnState
    ) => string = MultipleSelectComponent.toDefaultSQL
): Partial<ICompiledSQLTokens> {
    const where: Partial<ICompiledSQLTokens> = (() => {
        if (state.isFilterApplied)
            return {
                where: [toSql(columnSQL, state)],
            };
        return {};
    })();

    const split_group: Partial<ICompiledSQLTokens> = (() => {
        if (state.isSplitApplied)
            return {
                split_group: [columnSQL],
            };
        return {};
    })();
    return { ...where, ...split_group };
}

/**
 * `player_id` is a signed INT64 whose magnitude can exceed `Number.MAX_SAFE_INTEGER`, so it stays
 * a string in the text input and is cast only by BigQuery.
 */
const PLAYER_ID_PATTERN = /^-?(?:0|[1-9]\d{0,18})$/;
const PLAYER_ID_MAX = '9223372036854775807';
const PLAYER_ID_MIN_ABS = '9223372036854775808';

/**
 * Compiles SQL tokens for a player-id filter.
 *
 * The input is deliberately exact rather than a substring match. It is validated here because saved
 * views can restore state without passing through the text-input component.
 *
 * @param columnSQL sql of a column
 * @param state state of a column
 * @throws {Error} when a selected value is not a valid player id
 */
export function playerIdCompileSQLTokens(
    columnSQL: string,
    state: IQueryContextDatasourceColumnState
): Partial<ICompiledSQLTokens> {
    if (!state.isFilterApplied) {
        return {};
    }

    const playerId = state.value;
    const playerIdAbs =
        typeof playerId === 'string' && playerId.startsWith('-')
            ? playerId.slice(1)
            : playerId;
    const playerIdLimit =
        typeof playerId === 'string' && playerId.startsWith('-')
            ? PLAYER_ID_MIN_ABS
            : PLAYER_ID_MAX;
    const isInRange =
        typeof playerIdAbs === 'string' &&
        (playerIdAbs.length < PLAYER_ID_MAX.length ||
            (playerIdAbs.length === PLAYER_ID_MAX.length &&
                playerIdAbs <= playerIdLimit));

    if (
        typeof playerId !== 'string' ||
        !PLAYER_ID_PATTERN.test(playerId) ||
        !isInRange
    ) {
        throw new Error(
            `Invalid player id '${playerId}' in filter on ${columnSQL}. Expected an INT64 value.`
        );
    }

    return {
        where: [`${columnSQL} = CAST('${playerId}' AS INT64)`],
    };
}

/**
 * UBI-1363: projects whose segment filters use SegmentSqlTokensCompiler's per-group TVF.
 * An empty list restores TABLE_GET_SEGMENT_CONTAINER_ID_* everywhere.
 */
export const SEGMENT_GROUP_TVF_PROJECTS: GcpProjectId[] = [
    'mopa-b0dba',
    'am-mopa-dev',
];

/**
 * Compiles SQL tokens related to segment container ID based on the provided state and options.
 *
 * @param {ISegmentDatasourceColumnState} state - The state containing details of the segment datasource column.
 * @param {object} options - Optional parameters which include the GCP project ID.
 * @param {GcpProjectId} options.gcpProjectId - The ID of the GCP project.
 *
 * @returns {Partial<ICompiledSQLTokens>} Returns an object containing the compiled SQL tokens based on the provided state and options.
 *
 * @throws {Error} Throws an error if the options parameter is not provided.
 *
 * @example
 * const tokens = segmentContainerIdCompileSQLTokens(state, { gcpProjectId: 'my-project-id' });
 */
export function segmentContainerIdCompileSQLTokens(
    state: ISegmentDatasourceColumnState,
    options?: { gcpProjectId: GcpProjectId }
): Partial<ICompiledSQLTokens> {
    if (!options) {
        throw new Error(
            'options parameter for SEGMENT compileSQLTokens is required'
        );
    }
    // UBI-1363: per-group TVF instead of the container TVF
    if (SEGMENT_GROUP_TVF_PROJECTS.includes(options.gcpProjectId)) {
        return SegmentSqlTokensCompiler.compileTokens(state, options);
    }
    const isExcludeApplied = state.isExcludeApplied;

    if (isExcludeApplied) {
        if (state.isSplitApplied) {
            throw new Error(
                'Split is not supported for exclude mode in segment filter'
            );
        }

        const join: Partial<ICompiledSQLTokens> = (() => {
            if (state.isFilterApplied || state.isSplitApplied) {
                const AND_OR = state.value.operator;
                const segments = state.value.options
                    .map((option) => `'${option}'`)
                    .join(', '); //example: 'ContinuePlayingWindow1: None','AppRatePoint1: None'
                return {
                    join: [
                        `LEFT JOIN \`${options.gcpProjectId}.bi_config\`.TABLE_GET_SEGMENT_CONTAINER_ID_${AND_OR}([${segments}]) seg_excl
                ON ds.player_dim.segments.segment_container_id = seg_excl.segment_container_id`,
                    ],
                };
            }
            return {};
        })();

        const where: Partial<ICompiledSQLTokens> = (() => {
            if (state.isFilterApplied) {
                return {
                    where: [`seg_excl.segment_container_id IS NULL`],
                };
            }
            return {};
        })();
        return { ...where, ...join };
    } else {
        const join: Partial<ICompiledSQLTokens> = (() => {
            if (state.isFilterApplied || state.isSplitApplied) {
                const AND_OR = state.value.operator;
                const segments = state.value.options
                    .map((option) => `'${option}'`)
                    .join(', '); //example: 'ContinuePlayingWindow1: None','AppRatePoint1: None'
                return {
                    join: [
                        `JOIN \`${options.gcpProjectId}.bi_config\`.TABLE_GET_SEGMENT_CONTAINER_ID_${AND_OR}([${segments}]) seg 
                ON ds.player_dim.segments.segment_container_id = seg.segment_container_id`,
                    ],
                };
            }
            return {};
        })();

        const split_group: Partial<ICompiledSQLTokens> = (() => {
            if (state.isSplitApplied) {
                return { split_group: ['seg.split_group'] };
            }
            return {};
        })();

        return { ...split_group, ...join };
    }
}

export function segmentArrayCompileSQLTokens(
    state: ISegmentDatasourceColumnState,
    options?: { gcpProjectId: GcpProjectId }
): Partial<ICompiledSQLTokens> {
    if (!options)
        throw new Error(
            'options parameter for SEGMENT compileSQLTokens is required'
        );
    const isExcludeApplied = state.isExcludeApplied;

    if (isExcludeApplied) {
        throw new NotImplementedError(
            'Exclude mode is not supported for segment array filter'
        );
        // if (state.isSplitApplied) {
        //     throw new Error(
        //         'Split is not supported for exclude mode in segment filter'
        //     );
        // }

        // const join: Partial<ICompiledSQLTokens> = (() => {
        //     if (state.isFilterApplied || state.isSplitApplied) {
        //         const AND_OR = state.value.operator;
        //         const segments = state.value.options
        //             .map((option) => `'${option}'`)
        //             .join(', '); //example: 'ContinuePlayingWindow1: None','AppRatePoint1: None'
        //         return {
        //             join: [
        //                 `LEFT JOIN \`${options.gcpProjectId}.bi_config\`.TABLE_GET_SEGMENT_CONTAINER_ID_${AND_OR}([${segments}]) seg_excl
        //         ON ds.player_dim.segments.segment_container_id = seg_excl.segment_container_id`,
        //             ],
        //         };
        //     }
        //     return {};
        // })();

        // const where: Partial<ICompiledSQLTokens> = (() => {
        //     if (state.isFilterApplied) {
        //         return {
        //             where: [`seg_excl.segment_container_id IS NULL`],
        //         };
        //     }
        //     return {};
        // })();

        // return { ...where, ...join };
    } else {
        const segments_string = state.value.options
            .map((option) => `'${option}'`)
            .join(', ');
        const segments_count = state.value.options.length;

        const split_group: Partial<ICompiledSQLTokens> = (() => {
            if (state.isSplitApplied) {
                return {
                    split_group: [
                        `COALESCE(( SELECT STRING_AGG(d.segment, ' | ') FROM UNNEST(player_dim.segments.segments) segment WHERE segment IN (${segments_string})), 'Other')`,
                    ],
                };
            }
            return {};
        })();

        const where: Partial<ICompiledSQLTokens> = (() => {
            if (state.isFilterApplied) {
                const AND_OR = state.value.operator;

                switch (AND_OR) {
                    case 'OR':
                        return {
                            where: [
                                `EXISTS( SELECT 1 FROM UNNEST(player_dim.segments.segments) segment WHERE segment IN (${segments_string}))`,
                            ],
                        };
                    case 'AND':
                        return {
                            where: [
                                `ARRAY_LENGTH(ARRAY((SELECT 1 FROM UNNEST(player_dim.segments.segments) segment WHERE segment IN (${segments_string})))) = ${segments_count}`,
                            ],
                        };
                }
            }
            return {};
        })();

        return { ...split_group, ...where };
    }
}
